import { useCan } from '../store';
import { Resource } from '../api/authz';
import React, { useState } from 'react';
import HelpTip from './HelpTip';
import { ApiError, alertApprovalIfPending } from '../api/request';
import { listDict } from '../api/dict';
import { listMMGroups } from '../api/mmode';
import { dictOptionLabel, useDictOptions } from '../utils/dictOptions';
import { alertDialog } from '../utils/dialog';
import SearchSelect from './SearchSelect';

// 矩阵布局字段组的通用 展示+行内编辑 组件 (材料理化 / 设备动态字段 复用).
// 行来自枚举 (rowSource: dict 类目 或 'mm_group'=加工模式组) × 列来自字段定义;
// 值 {rowCode: {fieldKey: value}}, 保存整组覆盖 (空 = 不设置).

export interface MatrixField {
    key: string;
    label: string;
    unit?: string;
    /** 枚举选项: 非空时单元格渲染下拉 (值存字符串) */
    options?: string[];
    /** 字典来源类目 (SSOT enums_source): 编辑态渲染字典下拉, 只读态 code → label 不裸显 */
    optionsSource?: string;
}

export interface MatrixRow {
    code: string;
    label: string;
}

export type MatrixParams = Record<string, Record<string, number | string>>;

/** 行来源枚举 → 行列表: 'mm_group' 走加工模式组, 其余按 dict 类目. */
export async function loadMatrixRows(rowSource: string): Promise<MatrixRow[]> {
    if (rowSource === 'mm_group') {
        const gs = await listMMGroups();
        return gs.map((g) => ({ code: g.code, label: g.nameZh || g.code }));
    }
    const es = await listDict(rowSource);
    return es.map((e) => ({ code: e.code, label: e.label }));
}

export interface MatrixGroupEditorProps {
    /** RBAC 资源域 (material / device): 编辑入口按其 update 权隐藏 (2026-09-12) */
    resource: Resource;
    /** 组标题 (如 "光学参数 · 按激光波长" / "模式能力") */
    label: string;
    /** 数据来源标注 (可空) */
    src?: string;
    /** 矩阵表头第一列名 (如 "波长 (nm)" / "加工模式") */
    rowLabel?: string;
    rowSource?: string;
    fields: MatrixField[];
    rows: MatrixRow[];
    params: MatrixParams | null | undefined;
    /** 保存整组值; 返回后端 message (审批流提示用, 无则空串) */
    onSave: (params: MatrixParams) => Promise<string>;
    /** 保存成功后的回读 */
    onSaved: () => Promise<void> | void;
    /** 只读 (无编辑按钮) */
    readOnly?: boolean;
}

function MatrixGroupEditor({
    resource,
    label,
    src,
    rowLabel,
    rowSource,
    fields,
    rows,
    params,
    onSave,
    onSaved,
    readOnly,
}: MatrixGroupEditorProps) {
    const canUpdate = useCan(resource, 'update');
    const [edit, setEdit] = useState<Record<
        string,
        Record<string, string>
    > | null>(null);
    const [busy, setBusy] = useState(false);
    const values = (params || {}) as MatrixParams;
    // 字典来源列: 编辑态下拉选项 + 只读态 code → label
    const dictOpts = useDictOptions(fields.map((f) => f.optionsSource));

    const startEdit = () => {
        const next: Record<string, Record<string, string>> = {};
        for (const r of rows) {
            next[r.code] = {};
            for (const f of fields) {
                const v = values[r.code]?.[f.key];
                next[r.code][f.key] = v == null ? '' : String(v);
            }
        }
        setEdit(next);
    };

    const save = async () => {
        if (!edit) return;
        const out: MatrixParams = {};
        for (const [row, kv] of Object.entries(edit)) {
            for (const f of fields) {
                const raw = (kv[f.key] ?? '').trim();
                if (raw === '') continue;
                let val: number | string = raw;
                if (!f.options?.length) {
                    const n = Number(raw);
                    if (!Number.isFinite(n)) {
                        alertDialog(`「${f.label}」不是有效数字: ${raw}`);
                        return;
                    }
                    val = n;
                }
                (out[row] = out[row] || {})[f.key] = val;
            }
        }
        setBusy(true);
        try {
            const message = await onSave(out);
            await onSaved();
            setEdit(null);
            alertApprovalIfPending(message);
        } catch (e) {
            alertDialog(
                '保存失败: ' +
                    (e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e)),
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-2">
                {label}
                {src && (
                    <HelpTip
                        text={
                            <>
                                数据来源: <code>{src}</code>
                            </>
                        }
                    />
                )}
                {!readOnly && edit === null && (
                    <button
                        type="button"
                        className="ml-auto text-xs text-brand-600 hover:underline font-normal"
                        onClick={startEdit}
                        hidden={!canUpdate}
                    >
                        ✎ 编辑
                    </button>
                )}
            </div>
            <table className="w-full">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="th">{rowLabel || ''}</th>
                        {fields.map((f) => (
                            <th key={f.key} className="th" title={f.key}>
                                {f.label}
                                {f.unit ? ` (${f.unit})` : ''}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.code}>
                            <td className="td font-medium">{r.label}</td>
                            {fields.map((f) => {
                                if (edit) {
                                    const cur = edit[r.code]?.[f.key] ?? '';
                                    const setCell = (v: string) =>
                                        setEdit({
                                            ...edit,
                                            [r.code]: {
                                                ...(edit[r.code] || {}),
                                                [f.key]: v,
                                            },
                                        });
                                    const enumOpts = f.optionsSource
                                        ? dictOpts[f.optionsSource] || []
                                        : (f.options || []).map((o) => ({
                                              code: o,
                                              label: o,
                                          }));
                                    return (
                                        <td key={f.key} className="td">
                                            {enumOpts.length ||
                                            f.optionsSource ? (
                                                <SearchSelect
                                                    className="w-full"
                                                    value={cur}
                                                    onChange={(v) => setCell(v)}
                                                    placeholder="未设置"
                                                    options={enumOpts.map(
                                                        (o) => ({
                                                            value: o.code,
                                                            label: o.label,
                                                        }),
                                                    )}
                                                    clearable
                                                />
                                            ) : (
                                                <input
                                                    type="number"
                                                    step="any"
                                                    className="inp w-full font-mono"
                                                    value={cur}
                                                    onChange={(e) =>
                                                        setCell(e.target.value)
                                                    }
                                                />
                                            )}
                                        </td>
                                    );
                                }
                                const v = values[r.code]?.[f.key];
                                return (
                                    <td
                                        key={f.key}
                                        className={`td ${v == null ? 'text-slate-300' : 'font-mono'}`}
                                    >
                                        {v == null
                                            ? '—'
                                            : dictOptionLabel(
                                                  dictOpts,
                                                  f.optionsSource,
                                                  String(v),
                                              )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td
                                className="td text-slate-300"
                                colSpan={fields.length + 1}
                            >
                                行来源枚举「{rowSource}」暂无条目
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {edit && (
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        type="button"
                        className="btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => setEdit(null)}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary text-xs"
                        disabled={busy}
                        onClick={save}
                    >
                        {busy ? '保存中…' : '保存'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default MatrixGroupEditor;
